import { useContext, useEffect, useRef, useState } from 'react';
import { ModalMobile, ModalDesktop } from '@acko-tech/building-blocks.ui.modal';
import Header from 'widgets/header';
import NewPlan from './newPlan';
import Transactions from './transactions';
import { IOrder } from './types';
import { PageLoader } from 'utils/styles';
import { getImageBasePath } from 'utils/generics';
import ErrorPage from 'widgets/errorScreen';
import {
    SegmentEventNameTypes,
    SegmentEventPages,
} from 'utils/segment/constants';
import { MPosContext } from 'utils/context';
import {
    NewPlanStages,
    policyDetailsInitState,
} from 'components/newPlan/constants';
import * as Styles from './styles';
import { useViewport } from 'utils/responsive';
import LegalDocumentPendingState from './legalDocumentPending';
import CommissionEarned from 'components/dashboard/commissionEarned';
import TransactionsV2 from './transactionsV2';
import { getDealerDashboard } from './apis';
import GSTDetailsUpdateModal from './gstDetailsUpdateModal';
import sendSegmentEvent from 'utils/segment';

function Dashboard() {
    const {
        partnerId,
        legalDetailsUploadedStatus,
        setPlanDetails,
        setActiveStage,
        permissions,
        gstDetailsValid,
        userDetails,
    } = useContext(MPosContext);
    const { purchase_journey } = permissions;

    const { isMobile } = useViewport();

    const [pendingOrders, setPendingOrders] = useState<IOrder[]>([]);
    const [completedOrders, setCompletedOrders] = useState<IOrder[]>([]);
    const [orderList, setOrderList] = useState<IOrder[]>([]);
    const [pendingOrdersPage, setPendingOrdersPage] = useState(0);
    const [completedOrdersPage, setCompletedOrdersPage] = useState(0);
    const [ordersPage, setOrdersPage] = useState(0);
    const [hasMore, setHasMore] = useState({
        pending: true,
        completed: true,
        orders: true,
    });
    const [isPageLoading, setIsPageLoading] = useState(true);
    const [errorMesssage, setErrorMessage] = useState('');
    const [tabSelected, setTabSelected] = useState('all');
    const [searchValue, setSearchValue] = useState('');
    const prevSearchValue = useRef('');

    useEffect(() => {
        /**
         * view segment hook
         */

        sendSegmentEvent(
            SegmentEventNameTypes.VIEW,
            {
                view_name: SegmentEventPages.VIEW_HOMEPAGE_DEALER,
                page_type: SegmentEventPages.VIEW_MAIN_PAGE,
            },
            { userDetails, isMobile }
        );
        setPlanDetails(policyDetailsInitState);
        setActiveStage(NewPlanStages.MODEL);
    }, []);

    useEffect(() => {
        if (!partnerId || !permissions.group) {
            return;
        }
        import('./apis').then(async (req) => {
            const { status, data } = await req.getDealerDashboard(
                partnerId,
                permissions.group,
                0,
                '',
                purchase_journey,
                searchValue
            );
            if (status && data?.recent_orders) {
                const { pending_orders, completed_orders, orders } =
                    data.recent_orders;
                setPendingOrders(pending_orders || []);
                setCompletedOrders(completed_orders || []);
                setOrderList(orders || []);
                if (pending_orders?.length > 9) {
                    setPendingOrdersPage(1);
                } else {
                    setHasMore({
                        ...hasMore,
                        pending: false,
                    });
                }
                if (completed_orders?.length > 9) {
                    setCompletedOrdersPage(1);
                } else {
                    setHasMore({
                        ...hasMore,
                        completed: false,
                    });
                }
                if (orders?.length > 9) {
                    setOrdersPage(1);
                } else {
                    setHasMore({
                        ...hasMore,
                        orders: false,
                    });
                }
                setErrorMessage('');
            } else {
                setErrorMessage('Something went wrong.');
            }
            setIsPageLoading(false);
        });
    }, [partnerId, permissions.group]);

    useEffect(() => {
        if (prevSearchValue.current === searchValue) {
            return;
        }
        prevSearchValue.current = searchValue;

        let timeout: any;
        if (partnerId && permissions.group) {
            timeout = setTimeout(() => {
                setHasMore((prev) => ({ ...prev, orders: true }));
                setOrdersPage(0);
                import('./apis').then(async (req) => {
                    const { status, data } = await req.getDealerDashboard(
                        partnerId,
                        permissions.group,
                        0,
                        tabSelected === 'all' ? '' : tabSelected,
                        purchase_journey,
                        searchValue
                    );
                    if (status && data?.recent_orders) {
                        const { orders } = data.recent_orders;
                        setOrderList(orders || []);
                        if (orders?.length > 9) {
                            setOrdersPage(1);
                            setHasMore((prev) => ({ ...prev, orders: true }));
                        } else {
                            setHasMore((prev) => ({ ...prev, orders: false }));
                        }
                        setErrorMessage('');
                    } else {
                        setErrorMessage('Something went wrong.');
                    }
                });
            }, 500);
        }
        return () => clearTimeout(timeout);
    }, [
        searchValue,
        partnerId,
        permissions.group,
        tabSelected,
        purchase_journey,
    ]);

    const clearSearch = () => {
        setSearchValue('');
        prevSearchValue.current = '';
        if (partnerId && permissions.group) {
            setHasMore((prev) => ({ ...prev, orders: true }));
            setOrdersPage(0);
            import('./apis').then(async (req) => {
                const { status, data } = await req.getDealerDashboard(
                    partnerId,
                    permissions.group,
                    0,
                    tabSelected === 'all' ? '' : tabSelected,
                    purchase_journey,
                    ''
                );
                if (status && data?.recent_orders) {
                    const { orders } = data.recent_orders;
                    setOrderList(orders || []);
                    if (orders?.length > 9) {
                        setOrdersPage(1);
                        setHasMore((prev) => ({ ...prev, orders: true }));
                    } else {
                        setHasMore((prev) => ({ ...prev, orders: false }));
                    }
                    setErrorMessage('');
                } else {
                    setErrorMessage('Something went wrong.');
                }
            });
        }
    };

    const refreshDashbord = () => {
        import('./apis').then(async (req) => {
            const { status, data } = await req.getDealerDashboard(
                partnerId,
                permissions.group,
                0,
                '',
                purchase_journey,
                searchValue
            );
            if (status && data?.recent_orders) {
                const { pending_orders, completed_orders, orders } =
                    data.recent_orders;
                setPendingOrders(pending_orders || []);
                setCompletedOrders(completed_orders || []);
                setOrderList(orders || []);
                if (pending_orders?.length > 9) {
                    setPendingOrdersPage(1);
                } else {
                    setHasMore({
                        ...hasMore,
                        pending: false,
                    });
                }
                if (completed_orders?.length > 9) {
                    setCompletedOrdersPage(1);
                } else {
                    setHasMore({
                        ...hasMore,
                        completed: false,
                    });
                }
                if (orders?.length > 9) {
                    setOrdersPage(1);
                } else {
                    setHasMore({
                        ...hasMore,
                        orders: false,
                    });
                }
                setErrorMessage('');
            } else {
                setErrorMessage('Something went wrong.');
            }
            setIsPageLoading(false);
        });
    };

    const onLoadMoreV2 = async (
        selectedTab: 'pending' | 'completed' | 'all' | 'failed'
    ) => {
        if (selectedTab !== tabSelected) {
            import('./apis').then(async (req: any) => {
                const { status, data } = await req.getDealerDashboard(
                    partnerId,
                    permissions.group,
                    0,
                    selectedTab === 'all' ? '' : selectedTab,
                    purchase_journey,
                    searchValue
                );
                if (status && data?.recent_orders) {
                    const { orders } = data.recent_orders;
                    setOrderList(orders);
                    if (orders?.length > 9) {
                        setOrdersPage(1);
                    } else {
                        setHasMore({
                            ...hasMore,
                            orders: false,
                        });
                    }
                    setErrorMessage('');
                } else {
                    setErrorMessage('Something went wrong.');
                }
            });
            setTabSelected(selectedTab);
        } else {
            const { status, data } = await getDealerDashboard(
                partnerId,
                permissions.group,
                ordersPage,
                selectedTab === 'all' ? '' : selectedTab,
                purchase_journey,
                searchValue
            );
            if (status && data?.recent_orders) {
                const { orders } = data.recent_orders;
                if (orders?.length > 0) {
                    setOrderList(orderList.concat(orders));
                    setHasMore({
                        ...hasMore,
                        orders: orders.length > 9,
                    });
                    orders?.length > 9 && setOrdersPage(ordersPage + 1);
                }
            }
        }
    };

    const onLoadMore = (
        selectedTab: 'pending' | 'completed' | 'all' | 'failed'
    ) => {
        switch (purchase_journey) {
            case 'v1':
                onLoadMoreV2(selectedTab);
                return;

            default:
                import('./apis').then(async (req) => {
                    const page =
                        selectedTab === 'pending'
                            ? pendingOrdersPage
                            : completedOrdersPage;

                    const { status, data } = await req.getDealerDashboard(
                        partnerId,
                        permissions.group,
                        page,
                        selectedTab,
                        purchase_journey
                    );
                    if (status && data?.recent_orders) {
                        const { pending_orders, completed_orders } =
                            data.recent_orders;
                        if (selectedTab === 'pending') {
                            if (pending_orders?.length > 0) {
                                setPendingOrders(
                                    pendingOrders.concat(pending_orders)
                                );
                                setHasMore({
                                    ...hasMore,
                                    pending: pending_orders.length > 9,
                                });
                                pending_orders?.length > 9 &&
                                    setPendingOrdersPage(pendingOrdersPage + 1);
                            } else {
                                setHasMore({
                                    ...hasMore,
                                    pending: false,
                                });
                            }
                            return;
                        }
                        if (completed_orders?.length > 0) {
                            setCompletedOrders(
                                completedOrders.concat(completed_orders)
                            );
                            setHasMore({
                                ...hasMore,
                                completed: completed_orders.length > 9,
                            });
                            completed_orders?.length > 9 &&
                                setCompletedOrdersPage(completedOrdersPage + 1);
                        } else {
                            setHasMore({
                                ...hasMore,
                                completed: false,
                            });
                        }
                    }
                });
        }
    };

    const Modal: any = isMobile ? ModalMobile : ModalDesktop;

    if (isPageLoading) {
        return <PageLoader src={getImageBasePath('/gif/pageLoader.gif')} />;
    }

    if (errorMesssage) {
        return <ErrorPage />;
    }

    return (
        <>
            <Styles.Wrapper>
                <Header />
                <Styles.Content>
                    <Styles.Section $index={0}>
                        <NewPlan />
                    </Styles.Section>
                    {permissions.purchase_journey === 'v1' &&
                        permissions.permission_list.length > 0 &&
                        permissions.permission_list.includes(
                            'commissions_earned'
                        ) && (
                            <Styles.Section $index={1}>
                                <CommissionEarned partner={partnerId} />
                            </Styles.Section>
                        )}
                    <Styles.Section $index={2}>
                        {purchase_journey === 'v1' ? (
                            <TransactionsV2
                                hasMore={hasMore.orders}
                                onLoadMore={onLoadMore}
                                orders={orderList}
                                refresh={refreshDashbord}
                                searchValue={searchValue}
                                onSearchChange={setSearchValue}
                                clearSearch={clearSearch}
                            />
                        ) : (
                            <Transactions
                                pendingOrders={pendingOrders}
                                completedOrders={completedOrders}
                                hasMorePending={hasMore.pending}
                                hasMoreCompleted={hasMore.completed}
                                onLoadMore={onLoadMore}
                            />
                        )}
                    </Styles.Section>
                </Styles.Content>
            </Styles.Wrapper>
            {legalDetailsUploadedStatus && (
                <Modal
                    show={true}
                    urlhash={''}
                    onHide={() => {}}
                    padding={'20px'}
                    wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                    showOverlayBackground={true}
                    hideCloseButton={true}
                >
                    <LegalDocumentPendingState />
                </Modal>
            )}

            {!gstDetailsValid && (
                <Modal
                    show={true}
                    urlhash={''}
                    onHide={() => {}}
                    padding={'20px'}
                    maxWidth={'500px'}
                    wrapperStyles={`border-radius: 16px 16px 0px 0px;`}
                    showOverlayBackground={true}
                    hideCloseButton={true}
                >
                    <GSTDetailsUpdateModal />
                </Modal>
            )}
        </>
    );
}

export default Dashboard;
